import React, { useCallback, useMemo, memo } from 'react'
import useRowHydration from '../../../../../services/community/identity/library/useRowHydration'
import SystemMenu from '../../Library/ProfilePlaylistMenuOptions/SystemMenu'
import PlaylistSeeEntireLink from '../../Library/PlaylistSeeEntireLink/index'
import PlaylistOutline from '../../../../Icon/icons/PlaylistOutline/index'
import Clock from '../../../../Icon/icons/Clock/index'
import CommunityEmptyState from '../CommunityEmptyState/CommunityEmptyState'
import type { EditToolbarLabels } from '../CommunityProfileCarousels/index'
import type { PlaylistShell, EnrichedPlaylistItem } from '../../../../../services/community/types'
import RowEditDoneButton from './RowEditDoneButton'
import type { PlaylistRowContextValue } from './PlaylistRowContext'
import PlaylistRow from './PlaylistRow'

/**
 * Variant: Watch Later / Favorites.
 *
 * Shell-driven hydration via `useRowHydration` (matches custom rows).
 *
 * **§6.5 ratification: legacy menu path.** Renders `<SystemMenu>` — a bare
 * edit trigger (no dropdown). System playlists cannot be deleted, shared,
 * or copy-linked, so the explicit `SystemMenu` variant replaces the legacy
 * `<ProfilePlaylistMenuOptions isSystemPlaylist>` boolean. No
 * PlaylistSharingDropdown (system playlists don't expose visibility controls).
 * No PlaylistShareButton — legacy `/library` Watch Later + Favorites do not
 * render one.
 *
 * Edit mode: when `isEditing` is true, `nameAction` swaps to a Done button
 * (matches legacy `SystemPlaylistRow.tsx:153-162`) and the Body engages
 * the tile-level remove + reorder pills.
 */

export type SystemPlaylistAction = (
  playlistId: string,
  action: string,
  playlistType?: string,
  playlistName?: string,
) => void

export interface SystemPlaylistEmptyStateLabels {
  description: string
  whatNext: string
  browseGaiaLibrary: string
}

export interface SystemPlaylistShellRowProps {
  /** The PlaylistShell entity for the system playlist (defaultPlaylist or favoritesPlaylist). */
  playlist: PlaylistShell
  /** Display title — comes from the parent's resolved staticText. */
  label: string
  language: string
  jwt?: string
  /** Owner-side edit toggling. Pass `editingPlaylistId === playlist.id` from the parent. */
  isEditing?: boolean
  doneLabel?: string
  onDoneEditing?: () => void
  /** Optional row-level handlers — when present, the body wires edit-mode controls. */
  onRemoveItem?: (item: EnrichedPlaylistItem) => void
  onReorderItem?: (item: EnrichedPlaylistItem, newPosition: number) => void
  onAction?: SystemPlaylistAction
  seeMoreLabel: string
  showLessLabel?: string
  seeEntireTemplate: string
  viewAllRoute: string
  videosLabel: string
  zeroDurationLabel: string
  editToolbarLabels?: EditToolbarLabels
  /** Optional empty CTA — Watch Later / Favorites parents pass it; profile page omits. */
  emptyStateLabels?: SystemPlaylistEmptyStateLabels
  onWhatNextClick?: (prompt: string) => void
  onBrowseClick?: () => void
  customStats?: React.ReactNode
}

function SystemPlaylistShellRowImpl ({
  playlist,
  label,
  language,
  jwt,
  isEditing = false,
  doneLabel,
  onDoneEditing,
  onRemoveItem,
  onReorderItem,
  onAction,
  seeMoreLabel,
  showLessLabel,
  seeEntireTemplate,
  viewAllRoute,
  videosLabel,
  zeroDurationLabel,
  editToolbarLabels,
  emptyStateLabels,
  onWhatNextClick,
  onBrowseClick,
  customStats,
}: SystemPlaylistShellRowProps) {
  const { details, isHydrating } = useRowHydration(playlist, language, { jwt })
  const expectedItemCount = playlist.itemCount ?? 0
  // Skeleton path requires the shell to claim items the hydration hasn't yet
  // produced. For Watch Later / Favorites the shell's itemCount is the source
  // of truth (PlaylistShell selects itemCount directly).
  const isEmpty = !isHydrating && details.items.length === 0

  const ctx = useMemo<PlaylistRowContextValue>(() => ({
    playlistId: playlist.id,
    // Override displayed name with the parent's localized label — the shell's
    // raw playlistName for system playlists is the canonical "Watch Later" /
    // "Favorites" string in EN but the UI surfaces a translated label.
    playlistName: label,
    playlistType: playlist.playlistType || undefined,
    items: details.items,
    expectedItemCount,
    totalDuration: details.totalDuration ?? null,
    primaryTopic: details.primaryTopic,
    isHydrating,
    isEditing,
  }), [
    playlist.id, playlist.playlistType, label,
    details.items, details.totalDuration, details.primaryTopic,
    expectedItemCount, isHydrating, isEditing,
  ])

  const emptyStats = useMemo(() => (isEmpty ? (
    <div className="playlist-row-header__stats">
      <span className="playlist-row-header__stat playlist-row-header__stat--episodes">
        <PlaylistOutline aria-hidden="true" />
        {videosLabel}
      </span>
      <span className="playlist-row-header__stat">
        <Clock aria-hidden="true" />
        {zeroDurationLabel}
      </span>
    </div>
  ) : undefined), [isEmpty, videosLabel, zeroDurationLabel])

  // Stable SystemMenu edit handler — the previous inline arrow had fresh
  // identity each render and defeated `<SystemMenu>`'s `React.memo`.
  const handleSystemMenuEdit = useCallback((id: string, type?: string, name?: string) => {
    if (onAction) onAction(id, 'edit', type, name)
  }, [onAction])

  // nameAction: Done button when editing, SystemMenu (bare edit trigger)
  // otherwise. system playlists expose only edit; this uses the explicit
  // `<SystemMenu>` variant rather than threading an `isSystemPlaylist`
  // boolean. Onward to `onAction`, dispatch the legacy `(id, action, type,
  // name)` shape with `action='edit'`.
  //
  // Memoised so the `<PlaylistRow.Header>` slot forwards a stable element
  // ref through to `CommunityPlaylistRowHeader` (React.memo'd). Without
  // this, each row re-render minted a new ReactElement and the downstream
  // memo missed. See `MyPlaylistRow.tsx` for the canonical rationale.
  const nameAction = useMemo<React.ReactNode>(() => {
    if (isEditing && onDoneEditing && doneLabel) {
      return <RowEditDoneButton onClick={onDoneEditing} label={doneLabel} />
    }
    if (onAction) {
      return (
        <SystemMenu
          playlistId={playlist.id}
          playlistType={playlist.playlistType}
          onEdit={handleSystemMenuEdit}
        />
      )
    }
    return null
  }, [
    isEditing, onDoneEditing, doneLabel,
    onAction, playlist.id, playlist.playlistType, handleSystemMenuEdit,
  ])

  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    if (!onReorderItem) return
    const items = details.items as EnrichedPlaylistItem[]
    const moving = items[fromIndex]
    const dest = items[toIndex]
    if (
      moving
      && dest
      && 'playlistPosition' in dest
      && typeof dest.playlistPosition === 'number'
      && 'playlistPosition' in moving
    ) {
      onReorderItem(moving, dest.playlistPosition)
    }
  }, [onReorderItem, details.items])

  // Stable wrapper so `<PlaylistRow.Body>`'s `onRemoveItem` prop doesn't
  // remint per render — the inline cast-and-forward arrow used to recreate
  // the function on every parent re-render, cascading through Body into
  // `ResponsivePlaylistDisplay`.
  const handleRemove = useCallback((item: unknown) => {
    if (onRemoveItem) onRemoveItem(item as EnrichedPlaylistItem)
  }, [onRemoveItem])

  const videoCount = expectedItemCount || details.items.length
  const seeEntireLabel = seeEntireTemplate.replace('{count}', String(videoCount))

  const emptyStateNode = useMemo(() => (
    emptyStateLabels && onWhatNextClick && onBrowseClick ? (
      <CommunityEmptyState
        variant="compact"
        description={emptyStateLabels.description}
        primaryCta={{ label: emptyStateLabels.whatNext, onClick: onWhatNextClick }}
        secondaryCta={{ label: emptyStateLabels.browseGaiaLibrary, onClick: onBrowseClick }}
      />
    ) : null
  ), [
    emptyStateLabels?.description, emptyStateLabels?.whatNext, emptyStateLabels?.browseGaiaLibrary,
    onWhatNextClick, onBrowseClick,
  ])

  return (
    <PlaylistRow.Provider value={ctx}>
      <PlaylistRow.Frame
        className="profile-my-playlists__playlist-row"
        id={`system-playlist-${playlist.id}`}
      >
        <PlaylistRow.Header
          nameAction={nameAction}
          customStats={customStats ?? emptyStats}
        />
        <PlaylistRow.Body
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
          hideToggle
          editToolbarLabels={editToolbarLabels}
          onRemoveItem={onRemoveItem ? handleRemove : undefined}
          onReorderItem={onReorderItem ? handleReorder : undefined}
          emptyState={emptyStateNode}
        />
        {videoCount > 0 ? (
          <PlaylistSeeEntireLink
            to={viewAllRoute}
            videoCount={videoCount}
            label={seeEntireLabel}
          />
        ) : null}
      </PlaylistRow.Frame>
    </PlaylistRow.Provider>
  )
}

const SystemPlaylistShellRow = memo(SystemPlaylistShellRowImpl)
SystemPlaylistShellRow.displayName = 'SystemPlaylistShellRow'
export default SystemPlaylistShellRow
